import { Camera } from '@babylonjs/core/Cameras/camera';

import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { System, SystemEvent } from './system';
import { getDevice } from '../../Utils/html';
import { EXPORT_FILTER_PREFIX } from '../Tool/interface';

const getRgbArrayFromString = (rgb: string): Color3 => {
    if (!rgb) return Color3.White();
    const rgbString = (rgb.includes('a')) ? rgb.substring(5, rgb.length - 1) : rgb.substring(4, rgb.length - 1);
    const rgbArrayString = rgbString.replace(/ /g, '').split(',');
    const rgbArray = rgbArrayString.map((value) => parseFloat(value) / 255);
    return new Color3(rgbArray[0], rgbArray[1], rgbArray[2]);
};

export class SystemResponsive extends System {
    /**
    * Check size and platform on which the 3D is rendered
    */

    constructor(canvas: HTMLCanvasElement) {
        super(canvas);

        // Code to avoid mouse pointer showing when mesh is hovered but not visible
        this.scene.pointerMovePredicate = (m) => m.isPickable
            && m.isVisible
            && m.isReady()
            && m.isEnabled();

        this.scene.clearColor = new Color4(0, 0, 0, 0);

        // To avoid iphone flash on resize, we put resize here on every frame
        // Don't worry resize will be calculated only when needed
        // Actually it is called on every frame so not very performant
        // Temporarely removed it in case it creates other issues
        // this.scene.registerBeforeRender(() => {
        //     this.engine.resize();
        // });

        // Call to launch the loop, initialize with and height of canvas
        // Plus make a first resize check
        this.setResizeContainerLoop();

        // Useless because resize loop is already triggered
        // window.addEventListener('resize', () => {
        //     this.engine.resize();
        // });
    }

    private getBackgroundColor(): Color3 {
        let found = false;
        let parent = this.canvas as HTMLElement;
        while (!found && parent !== document.body) {
            const style = window.getComputedStyle(parent, null);
            const color = style.getPropertyValue('background-color');
            if (color !== 'rgba(0, 0, 0, 0)') {
                found = true;
                return getRgbArrayFromString(color);
            }
            parent = parent.parentNode;
        }
        return Color3.White();
    }

    private sizeCheckInterval = 500;

    // The only way to make sure the scene is always fitted with the container
    // is to have a timer checking for changes
    // window resize does not always work in some specific cases
    private setResizeContainerLoop() {
        this.checkSizeAndBackground();
        setInterval(() => {
            this.checkSizeAndBackground();
        }, this.sizeCheckInterval);
    }

    private isBackgroundNeeded() {
        let need = false;

        this.scene.meshes.forEach((m) => {
            const { material, name } = m;
            if (material && !name.includes(EXPORT_FILTER_PREFIX)) {
                const {
                    alpha, albedoTexture, useAlphaFromAlbedoTexture, refractionTexture
                } = material;
                if (refractionTexture) need = true;
                //! Logos in venum are transparent but do not need background
                //! So keep only refraction test for real glass material
                // if (useAlphaFromAlbedoTexture || alpha !== 1) {
                //     need = true;
                //     console.log(1, m);
                // }
                // if (albedoTexture) {
                //     const { hasAlpha, getAlphaFromRGB } = albedoTexture;
                //     if (hasAlpha || getAlphaFromRGB) {
                //         console.log(2, m);
                //         need = true;
                //     }
                // }
            }
        });
        return need;
    }

    private checkBackgroundChange() {
        if (this.isBackgroundNeeded()) {
            const backgroundColor = this.getBackgroundColor();
            this.scene.clearColor = backgroundColor.toColor4(1);
        }
    }

    private checkSizeAndBackground() {
        this.checkBackgroundChange();
        this.checkSizeChange();
    }

    protected checkSizeChange(): void {
        const canvasChanged = this.checkCanvasSize();
        const windowChanged = this.checkWindowSize();
        const platformChanged = this.checkPixelRatio();
        if (canvasChanged || windowChanged || platformChanged) this.updateSize();
    }

    /**
     * Width of canvas
     */
    private canvasWidth = 100;

    /**
     * Height of canvas
     */
    private canvasHeight = 100;

    private checkCanvasSize(): boolean {
        const newWidth = this.canvas.offsetWidth;
        const newHeight = this.canvas.offsetHeight;
        if (newWidth !== this.canvasWidth || newHeight !== this.canvasHeight) {
            this.canvasWidth = newWidth;
            this.canvasHeight = newHeight;
            return true;
        }
        return false;
    }

    /**
     * Width of window
     */
    private windowWidth = 100;

    /**
     * Height of window
     */
    private windowHeight = 100;

    private checkWindowSize(): boolean {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        if (newWidth !== this.windowWidth || newHeight !== this.windowHeight) {
            this.windowWidth = newWidth;
            this.windowHeight = newHeight;
            return true;
        }
        return false;
    }

    public isOnMobile = false;

    public checkDevice(): boolean {
        const device = getDevice();
        if (device === 'iOS' || device === 'Androïd') this.isOnMobile = true;
        else this.isOnMobile = false;
        return this.isOnMobile;
    }

    /**
     * Scale between the scene size and the container size
     */
    protected pixelRatio = 1;

    public checkPixelRatio(): boolean {
        const onMobile = this.checkDevice();
        if (onMobile) {
            this.maxScaling = 2;
        } else {
            this.maxScaling = 1;
        }

        const { devicePixelRatio } = window;
        const newPixelRatio = Math.min(this.maxScaling, devicePixelRatio);

        if (newPixelRatio !== this.pixelRatio) {
            this.pixelRatio = newPixelRatio;
            return true;
        }
        return false;
    }

    /**
     * Get the current browser
     */
    private browser: string;

    public getBrowser(): string {
        const nav = navigator.userAgent;
        if ((nav.indexOf('Opera') || nav.indexOf('OPR')) !== -1) {
            this.browser = 'Opera';
        } else if (nav.indexOf('Edge') !== -1) {
            this.browser = 'Edge';
        } else if (nav.indexOf('Chrome') !== -1) {
            this.browser = 'Chrome';
        } else if (nav.indexOf('Safari') !== -1) {
            this.browser = 'Safari';
        } else if (nav.indexOf('Firefox') !== -1) {
            this.browser = 'Firefox';
        } else if ((nav.indexOf('MSIE') !== -1) || (!!document.documentMode === true)) {
            this.browser = 'IE';
        } else {
            this.browser = 'unknown';
        }
        return this.browser;
    }

    /**
     * Should the system change Scene field of view to adapt to screen size
     */
    private horizontalFixed = false;

    /**
     * Change horizontalFixed option
     */
    public setHorizontalFixed(horizontalFixed: boolean): void {
        this.horizontalFixed = horizontalFixed;
        const fovMode = (horizontalFixed)
            ? Camera.FOVMODE_HORIZONTAL_FIXED
            : Camera.FOVMODE_VERTICAL_FIXED;
        if (this.scene.activeCameras.length) {
            this.scene.activeCameras.forEach((camera) => {
                camera.fovMode = fovMode;
            });
        }
        if (this.scene.activeCamera) {
            this.scene.activeCamera.fovMode = fovMode;
        }
    }

    public resetHorizontalFixed(): void {
        this.setHorizontalFixed(this.horizontalFixed);
    }

    /**
     * Ratio between height of width of the scene container
     */
    public containerRatio = 1;

    /**
     * Width of the scene rendered
     */
    public renderWidth = 100;

    /**
     * Height of the scene rendered
     */
    public renderHeight = 100;

    public updateSize(): void {
        // Can't trust engine because qualitySystem will alter rending size
        // this.renderWidth = this.engine.getRenderWidth();
        // this.renderHeight = this.engine.getRenderHeight();

        this.renderWidth = this.canvasWidth * this.pixelRatio;
        this.renderHeight = this.canvasHeight * this.pixelRatio;

        this.containerRatio = this.renderWidth / this.renderHeight - 1;
        // Keep that for test purpose
        // console.log(window.orientation, window.devicePixelRatio)
        // console.log(this.containerWidth, this.containerHeight)
        // console.log(this.containerRatio)

        // if (this.launched) {
        this.checkVisible();
        //! if container not diplayed, engine.resize can double
        //! the canvas size and make the window explode
        if (this.containerVisible) this.engine.resize();
        this.checkFixedSideRatio();
        this.notify(SystemEvent.Resize, 0);
        // }
    }

    protected checkFixedSideRatio(): void {
        if (this.containerRatio > 0) this.setHorizontalFixed(false);
        else this.setHorizontalFixed(true);
    }
}
